import React, { useEffect } from 'react';
import { Navigate, Outlet, useOutletContext } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Hub } from 'aws-amplify';
import Loading from '../components/layout/Loading';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { useAuthenticatedUser } from '../hooks/useAuthenticatedUser';
import { RequestStatus } from '../redux/utils';
import { readCurrentUser, userGrants } from '../redux/slices/user';
import { readPreferences } from '../redux/slices/preference';
import { fetchWalletCategories } from '../redux/slices/walletCategories';
import { listNotifications, listSharedWalletNotifications } from '../redux/slices/notification';
import { fetchFeatureFlags } from '../redux/slices/featureFlags';
import { getCachedAuthUser } from '../utils/cachedAuth';
import { CelebrationProvider } from '../components/ui/organisms/Celebration';
import BadgeCelebrationWatcher from '../components/ui/organisms/Celebration/BadgeCelebrationWatcher';
import DailyLoginTracker from '../components/DailyLoginTracker';

export type LayoutContext = {
	setHeader: (header: React.ReactElement | undefined) => void;
	setActions: (actions: React.ReactElement | undefined) => void;
};

// Module-level flag: the core data load fires once per app lifecycle, not per layout mount.
// Reset on sign-out/sign-in/token failure so re-login triggers a fresh load.
let hasStartedEarlyLoad = false;
Hub.listen('auth', ({ payload }) => {
	if (payload.event === 'signOut' || payload.event === 'signIn' || payload.event === 'tokenRefresh_failure') {
		hasStartedEarlyLoad = false;
	}
});

/**
 * Persistent layout route — MainLayout stays mounted across all protected child route
 * navigations. This is the single enforcement boundary for protected access:
 *   1. Cognito auth status (configuring / unauthenticated).
 *   2. Core user record load (so child layout components never see undefined user state).
 *   3. App-level MFA enforcement.
 *
 * MFA is enforced HERE rather than in each page's `withProtected` so that a user who has not
 * satisfied MFA never mounts the chrome at all — the redirect is computed synchronously during
 * render via <Navigate>, eliminating the "page loads then bounces" flash that an effect-based
 * redirect inside a child route produced. It fails SECURE: while feature flags or the user
 * record are unresolved, MFA is treated as still-required.
 */
const ProtectedLayout = () => {
	const { authStatus } = useAuthenticator();
	const dispatch = useAppDispatch();
	const { user, readUserRequest, authenticated } = useAuthenticatedUser();
	const mfaSetupFlag = useAppSelector((state) => state.featureFlags?.flags?.['mfa-setup-enabled']);
	const featureFlagsLoaded = useAppSelector((state) => state.featureFlags?.loaded);
	const [header, setHeader] = React.useState<React.ReactElement | undefined>(undefined);
	const [actions, setActions] = React.useState<React.ReactElement | undefined>(undefined);

	// Core data load: don't wait for the useAuthenticator state machine.
	// Auth.currentAuthenticatedUser() validates tokens independently. Fired once and shared by
	// every protected child route via Redux. Lives here (not in withProtected) so the data is
	// available even when access is gated below and no child page has mounted yet.
	useEffect(() => {
		// Skip the auth/data bootstrap for a definitively logged-out user — they're redirected to
		// /login below, so probing the Cognito session here is wasted work on the redirect path.
		// (Still fires during 'configuring' so the load starts optimistically for real sessions.)
		if (authStatus === 'unauthenticated') return;
		if (hasStartedEarlyLoad) return;
		hasStartedEarlyLoad = true;
		performance.mark('withProtected:earlyLoad:start');

		getCachedAuthUser()
			.then(() => {
				performance.mark('withProtected:authResolved');
				performance.measure(
					'withProtected:authDuration',
					'withProtected:earlyLoad:start',
					'withProtected:authResolved'
				);
				performance.clearMarks('withProtected:authResolved');
				dispatch(readCurrentUser());
				dispatch(readPreferences());
				dispatch(fetchWalletCategories());
				dispatch(userGrants());
				dispatch(listNotifications());
				dispatch(listSharedWalletNotifications());
				dispatch(fetchFeatureFlags());
			})
			.catch(() => {
				hasStartedEarlyLoad = false;
				performance.clearMarks('withProtected:earlyLoad:start');
			});
	}, [dispatch, authStatus]);

	// MFA enforcement decision. Fails SECURE: while flags are unresolved MFA is still required;
	// the disable escape hatch only applies once flags have loaded AND the flag is explicitly
	// false. `MFACompleted` / `SkipThisSession` are set by the TOTPEnforcer setup/skip flow.
	const mfaEnforcementDisabled = featureFlagsLoaded ? mfaSetupFlag === false : false;
	const mfaCompleted = sessionStorage.getItem('MFACompleted') === 'true';
	const mfaSkippedThisSession = sessionStorage.getItem('SkipThisSession') === 'true';
	const mfaSatisfiedBySkip = mfaSkippedThisSession && (user?.totpSkipCount ?? 0) <= 3;
	const mfaBlocked = !mfaEnforcementDisabled && !user?.userMFASet && !mfaCompleted && !mfaSatisfiedBySkip;

	if (authStatus === 'configuring') {
		return <Loading />;
	}

	if (authStatus === 'unauthenticated') {
		return <Navigate to="/login" replace />;
	}

	// Authenticated but the user record hasn't resolved yet — wait. Deciding MFA before the
	// record loads would bounce a legitimate MFA-satisfied user back to /login on every refresh.
	if (
		authenticated &&
		readUserRequest.status !== RequestStatus.SUCCESS &&
		readUserRequest.status !== RequestStatus.ERROR
	) {
		return <Loading />;
	}

	// User record failed to load — redirect to /error from the enforcement boundary so no chrome
	// or child route mounts with an unresolved user record (avoids a render-then-bounce flash).
	if (authenticated && readUserRequest.status === RequestStatus.ERROR) {
		return <Navigate to="/error" replace />;
	}

	// MFA not satisfied — redirect synchronously, before any chrome renders. The /login route
	// re-renders the authenticated TOTPEnforcer (Cognito session is still valid).
	if (authenticated && readUserRequest.status === RequestStatus.SUCCESS && mfaBlocked) {
		return <Navigate to="/login" replace />;
	}

	return (
		<CelebrationProvider>
			<MainLayout header={header} actions={actions}>
				<Outlet context={{ setHeader, setActions } satisfies LayoutContext} />
			</MainLayout>
			<BadgeCelebrationWatcher />
			<DailyLoginTracker />
		</CelebrationProvider>
	);
};

export default ProtectedLayout;

export function useLayoutContext() {
	return useOutletContext<LayoutContext>();
}
